import type { EmissionEvidenceListItem } from '../../types/emissions';
import { getMimeThumb } from '../../utils/evidenceDisplay';

export const SORT_LABELS = {
  recent: 'Most recent',
  name: 'Name A→Z',
  size: 'Size (largest)',
} as const;

export type SortKey = keyof typeof SORT_LABELS;

const SORT_COMPARATORS: Record<SortKey, (a: EmissionEvidenceListItem, b: EmissionEvidenceListItem) => number> = {
  recent: (a, b) => (b.created ?? '').localeCompare(a.created ?? ''),
  name: (a, b) => a.originalName.localeCompare(b.originalName),
  size: (a, b) => (b.fileSize ?? 0) - (a.fileSize ?? 0),
};

export const sortItems = (items: EmissionEvidenceListItem[], sort: SortKey): EmissionEvidenceListItem[] =>
  [...items].sort(SORT_COMPARATORS[sort]);

/**
 * Single pass over the page items: extension lookup, type-filter, and
 * by-type bucketing all happen together to avoid three independent
 * iterations + three `getMimeThumb` calls per row.
 */
export interface ItemDerivation {
  filtered: EmissionEvidenceListItem[];
  availableExts: string[];
  byType: Record<string, number>;
}

export const deriveItems = (
  items: EmissionEvidenceListItem[],
  fileType: string,
): ItemDerivation => {
  const filtered: EmissionEvidenceListItem[] = [];
  const exts = new Set<string>();
  const byType: Record<string, number> = {};
  for (const item of items) {
    const { ext } = getMimeThumb(item.mimeType, item.originalName);
    exts.add(ext);
    byType[ext] = (byType[ext] ?? 0) + 1;
    if (!fileType || ext === fileType) {
      filtered.push(item);
    }
  }
  return { filtered, availableExts: [...exts].sort(), byType };
};
